import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dropzone from 'react-dropzone';
import { useSelector } from 'react-redux';
import format from 'date-fns/format';

import { useHttp } from 'hooks/http.hook';
import { serialize } from 'object-to-formdata';
import { MESSAGES } from 'endpointsConst';

import { FORMAT_DATE } from 'constants/time';
import { KEYS } from 'constants/keyCode';
import { DEFAULT_FILE_FORMATS, FILE_SIZE, LARGE_FILE_ERROR, INVALID_FILE_ERROR } from 'constants/files';
import InputError, { ERROR_PLACE } from 'components/InputsField/InputError';
import { showErrorMessage } from 'helpers/notifications';
import { playMessageSentSound } from '@/utils/notificationSounds';

import ChatMenuButton from 'components/common/ChatMenuButton';

import styles from './styles.module.scss';

const FILES_COUNT = 2;

const SendMessageForm = ({ roomId, user, item, type, handleChatWindowClose, onReplyMessage, replyEntity }) => {
  const { t } = useTranslation();
  const [messageValue, setMessageValue] = useState('');

  const [userId, setUserId] = useState(user.id);
  const [isUserBlocked, setIsUserBlocked] = useState(user.isBlock);
  const [isUserContact, setIsUserContact] = useState(user.isContact);

  const [files, setFiles] = useState([]);
  const lang = useSelector((state) => state.settings.lang);
  const { request, loading, error } = useHttp();

  const canSendMessage = useMemo(
    () => (messageValue.length > 0 || files.length > 0) && !loading,
    [messageValue, files, loading],
  );

  const isBlockedUser = useMemo(() => isUserBlocked || user.isCustomerBlocked, [isUserBlocked, user.isCustomerBlocked]);

  const handleKeyDown = (e) => {
    if (e.keyCode === KEYS.ENTER && !e.shiftKey) {
      handleFormSubmit(e);
    }
  };

  const entityFormatted = (item) => ({
    id: item.id,
    route_loading: `${item.loading_address.country_code} ${
      item.loading_address.city || item.loading_address.state_province || item.loading_address.country
    }`,
    route_unloading: `${item.unloading_address.country_code} ${
      item.unloading_address.city || item.unloading_address.state_province || item.unloading_address.country
    }`,
    loading_from: format(new Date(item.loading_from), FORMAT_DATE),
    unloading_to: format(new Date(item.unloading_to), FORMAT_DATE),
  });

  const handleSendMessage = useCallback(
    (data) => {
      const formData = serialize(data, {
        indices: true,
      });

      files &&
        files.forEach((item) => {
          formData.append('chat', item);
        });

      return request(MESSAGES, 'post', formData);
    },
    [files, request],
  );

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (canSendMessage) {
      const entity = replyEntity || item ? entityFormatted(replyEntity || item) : null;

      const requestData = {
        to_user_id: user.id,
        message: messageValue,
        entity,
        type: (replyEntity && replyEntity.type) || type,
        lang,
        isReply: Boolean(replyEntity && replyEntity.id),
        conversationId: roomId || null,
      };

      await handleSendMessage(requestData);

      playMessageSentSound();

      if (files.length > 0 && !error) {
        setFiles([]);
      }
      setMessageValue('');
      if (replyEntity && replyEntity.id) {
        onReplyMessage(null);
      }
    }
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (files.length < FILES_COUNT && files.length + acceptedFiles.length <= FILES_COUNT) {
        setFiles([...files, ...acceptedFiles]);
      } else {
        const firstFiles = [...files, ...acceptedFiles].slice(0, FILES_COUNT);
        setFiles(firstFiles);
      }
    },
    [files],
  );

  const handleDeleteFile = useCallback(
    (deletedFile) => {
      const filteredFiles = files.filter(
        (file) => file.path !== deletedFile.path && file.lastModified !== deletedFile.lastModified,
      );
      setFiles(filteredFiles);
    },
    [files],
  );

  const getTextAreaProps = (rootProps) => {
    delete rootProps.onClick;
    return rootProps;
  };

  const handleDropReject = useCallback(
    (e) => {
      if (e[0].errors.find((error) => error.code === LARGE_FILE_ERROR)) {
        showErrorMessage(t('errors.fileSize'));
      }
      if (e[0].errors.find((error) => error.code === INVALID_FILE_ERROR)) {
        showErrorMessage(t('errors.fileExtension'));
      }
    },
    [t],
  );
  useEffect(() => {
    if (isBlockedUser) {
      setMessageValue('');
    }
  }, [isBlockedUser]);

  useEffect(() => {
    if (user.id !== userId) {
      setUserId(user.id);
    }
  }, [userId, user.id, user.isBlock, isUserBlocked]);

  useEffect(() => {
    if (user.isBlock !== isUserBlocked) {
      setIsUserBlocked(user.isBlock);
    }
  }, [user.isBlock, isUserBlocked]);

  useEffect(() => {
    if (user.isContact !== isUserContact) {
      setIsUserContact(user.isContact);
    }
  }, [user.isContact, isUserContact]);

  useEffect(() => {
    if (user.id !== userId) {
      setUserId(user.id);
    }
  }, [user.id, userId]);

  return (
    <form className={styles.form} onSubmit={handleFormSubmit}>
      {files.length > 0 && (
        <div className={styles['form__file-wrapper']}>
          {files.map((file, index) => (
            <div key={index} className={styles.form__file}>
              <span>{file.name}</span>
              <button type="button" className={styles['form__delete-file']} onClick={() => handleDeleteFile(file)} />
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className={styles['form__file-wrapper']}>
          {error.data.errors.map((error, index) => (
            <InputError key={index} message={t(error.type)} mode={ERROR_PLACE.static} />
          ))}
        </div>
      )}
      <Dropzone
        onDrop={onDrop}
        name="message_files"
        maxSize={FILE_SIZE}
        accept={DEFAULT_FILE_FORMATS}
        disabled={isBlockedUser}
        onDropRejected={handleDropReject}
      >
        {({ getRootProps, getInputProps }) => (
          <>
            <div {...getTextAreaProps(getRootProps())}>
              <textarea
                className={styles.form__message}
                placeholder={isBlockedUser ? t('dialog.bannedMessage') : t('dialog.messagePlaceholder')}
                onKeyDown={handleKeyDown}
                value={messageValue}
                onChange={(e) => setMessageValue(e.target.value)}
                disabled={isBlockedUser}
              />
            </div>
            <div className={styles.form__actions}>
              <ChatMenuButton
                contact={{
                  employee: {
                    ...user,
                    isBlock: isUserBlocked,
                    isContact: isUserContact,
                  },
                }}
                conversationId={roomId}
                handleChatWindowClose={handleChatWindowClose}
                setIsUserBlocked={setIsUserBlocked}
                setIsUserContact={setIsUserContact}
              />
              <div className={styles.form__wrapper}>
                <input {...getInputProps()} className={styles.form__attachment} />
                {!isBlockedUser && (
                  <button onClick={getRootProps().onClick} className={styles.form__attachment} type="button" />
                )}
                <button className={styles['form__send-button']} type="submit" disabled={!canSendMessage}>
                  <svg className={styles['form__send-logo']} width="18.306" height="18.811" viewBox="0 0 18.306 18.811">
                    <path
                      d="M3.009,23.311l18.3-9.405L3.009,4.5,3,11.815l13.075,2.09L3,16Z"
                      transform="translate(-3 -4.5)"
                      fill="#fff"
                    />
                  </svg>
                  {t('dialog.send_button')}
                </button>
              </div>
            </div>
          </>
        )}
      </Dropzone>
    </form>
  );
};

export default SendMessageForm;
